import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { bulkUpdateClients } from '../../../utils/clientService';

const BulkEditModal = ({ isOpen, onClose, selectedClientIds, onSuccess }) => {
  const [formData, setFormData] = useState({
    engagement_level: '',
    location: ''
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState('');

  const engagementOptions = [
    { value: '', label: 'No change' },
    { value: 'High', label: 'High' },
    { value: 'Medium', label: 'Medium' },
    { value: 'Low', label: 'Low' }
  ];

  const locationOptions = [
    { value: '', label: 'No change' },
    { value: 'Downtown', label: 'Downtown' },
    { value: 'Suburbs', label: 'Suburbs' },
    { value: 'Westside', label: 'Westside' },
    { value: 'Eastside', label: 'Eastside' },
    { value: 'Northside', label: 'Northside' },
    { value: 'Southside', label: 'Southside' }
  ];

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    // Filter out empty values
    const updates = {};
    if (formData?.engagement_level) {
      updates.engagement_level = formData.engagement_level;
    }
    if (formData?.location) {
      updates.location = formData.location;
    }

    // Check if at least one field is being updated
    if (Object.keys(updates).length === 0) {
      setError('Please select at least one field to update');
      return;
    }

    setIsUpdating(true);
    setError('');

    try {
      const { error: updateError } = await bulkUpdateClients(selectedClientIds, updates);
      
      if (updateError) {
        setError(updateError?.message || 'Failed to update clients');
      } else {
        onSuccess?.();
        handleClose();
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleClose = () => {
    if (!isUpdating) {
      setFormData({
        engagement_level: '',
        location: ''
      });
      setError('');
      onClose?.();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm animate-fade-in flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg elevation-4 w-full max-w-md animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Bulk Edit Clients</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Update {selectedClientIds?.length} client{selectedClientIds?.length !== 1 ? 's' : ''}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            disabled={isUpdating}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 flex items-center space-x-2">
              <Icon name="AlertCircle" size={16} className="text-destructive" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          <Select
            label="Engagement Level"
            value={formData?.engagement_level}
            onValueChange={(value) => handleInputChange('engagement_level', value)}
            options={engagementOptions}
            disabled={isUpdating}
          />

          <Select
            label="Location"
            value={formData?.location}
            onValueChange={(value) => handleInputChange('location', value)}
            options={locationOptions}
            disabled={isUpdating}
          />

          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isUpdating}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              disabled={isUpdating}
              loading={isUpdating}
            >
              Update Clients
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BulkEditModal;

